import { useCallback, useEffect, useState } from "react";
import { Icon } from "@/components/icon/Icon";
import { api, ApiError } from "@/lib/api";
import { formatKst } from "@/lib/datetime";
import {
  CATEGORY_LABEL,
  PLATFORM_LABEL,
  validatePortfolioItems,
} from "@/lib/portfolio-draft";
import { moveItem } from "@/lib/pricing-draft";
import { iconButtonClass, inputClass } from "@/pages/admin/fieldClasses";
import type { PortfolioDTO, PortfolioItem } from "@/types/portfolio";

type Platform = PortfolioItem["platform"][number];

const PLATFORMS = Object.keys(PLATFORM_LABEL) as Platform[];
const CATEGORIES = Object.keys(CATEGORY_LABEL) as PortfolioItem["category"][];

/** 커버·로고 한 칸 — 경로 직접 입력과 파일 업로드가 같은 값을 채워요. */
function AssetField({
  id,
  label,
  placeholder,
  value,
  token,
  onChange,
  onAuthError,
}: {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  token: string;
  onChange: (next: string) => void;
  onAuthError: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { url } = await api.post<{ url: string }>("/admin/portfolio/image", form, token);
      onChange(url);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onAuthError();
        return;
      }
      setError(err instanceof ApiError ? err.message : "이미지를 올리지 못했어요.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="wds-label-1 text-label-strong">
        {label}
      </label>
      <div className="flex items-center gap-2">
        {value && (
          <img
            src={value}
            alt=""
            className="size-10 shrink-0 rounded-md bg-background-alternative object-contain"
          />
        )}
        <input
          id={id}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
        />
      </div>
      <label className="wds-caption-1 flex cursor-pointer items-center gap-1.5 text-primary">
        <input
          type="file"
          accept="image/webp,image/png,image/jpeg,image/svg+xml"
          disabled={uploading}
          onChange={(e) => {
            void handleFile(e.target.files?.[0]);
            e.target.value = "";
          }}
          className="hidden"
        />
        {uploading ? "올리는 중…" : "파일 올리기"}
      </label>
      {error && (
        <output className="wds-caption-1 text-status-negative">{error}</output>
      )}
    </div>
  );
}

const emptyItem = (): PortfolioItem => ({
  id: "",
  name: "",
  tagline: "",
  description: "",
  category: "own",
  platform: ["web"],
});

function ItemCard({
  item,
  index,
  total,
  token,
  onChange,
  onMove,
  onRemove,
  onAuthError,
}: {
  item: PortfolioItem;
  index: number;
  total: number;
  token: string;
  onChange: (patch: Partial<PortfolioItem>) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
  onAuthError: () => void;
}) {
  const field = (name: string) => `portfolio-${index}-${name}`;

  return (
    <li className="flex flex-col gap-4 rounded-lg bg-background-elevated p-5 shadow-normal">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          {item.logo && (
            <img src={item.logo} alt="" className="size-6 shrink-0 rounded-md object-contain" />
          )}
          <span className="wds-headline-2 truncate text-label-strong">
            {item.name.trim() || "(이름 없음)"}
          </span>
          <span className="wds-caption-1 shrink-0 rounded-full bg-background-alternative px-2 py-0.5 text-label-assistive tabular-nums">
            {index + 1}/{total}
          </span>
          {item.upcoming && (
            <span className="wds-caption-1 shrink-0 rounded-full bg-fill-normal px-2 py-0.5 text-label-neutral">
              출시 전
            </span>
          )}
          {item.hidden && (
            <span className="wds-caption-1 shrink-0 rounded-full bg-fill-normal px-2 py-0.5 text-label-assistive">
              숨김
            </span>
          )}
        </div>
        <div className="flex shrink-0 gap-1">
          <button
            type="button"
            aria-label={`${index + 1}번 위로`}
            disabled={index === 0}
            onClick={() => onMove(-1)}
            className={iconButtonClass}
          >
            ↑
          </button>
          <button
            type="button"
            aria-label={`${index + 1}번 아래로`}
            disabled={index === total - 1}
            onClick={() => onMove(1)}
            className={iconButtonClass}
          >
            ↓
          </button>
          <button
            type="button"
            aria-label={`${index + 1}번 삭제`}
            onClick={onRemove}
            className={`${iconButtonClass} text-status-negative`}
          >
            ✕
          </button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor={field("id")} className="wds-label-1 text-label-strong">
            id
          </label>
          <input
            id={field("id")}
            value={item.id}
            onChange={(e) => onChange({ id: e.target.value })}
            className={inputClass}
          />
          <span className="wds-caption-1 text-label-assistive">
            영소문자·숫자·하이픈. 방문 추적 이벤트에 그대로 쓰여서 한 번 정하면 바꾸지 않는 게 좋아요.
          </span>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor={field("name")} className="wds-label-1 text-label-strong">
            서비스명
          </label>
          <input
            id={field("name")}
            value={item.name}
            onChange={(e) => onChange({ name: e.target.value })}
            className={inputClass}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor={field("tagline")} className="wds-label-1 text-label-strong">
          한 줄 소개
        </label>
        <input
          id={field("tagline")}
          value={item.tagline}
          onChange={(e) => onChange({ tagline: e.target.value })}
          className={inputClass}
        />
        <span className="wds-caption-1 text-label-assistive">
          카드에 보이는 한 줄이에요. 크롤러용 본문·llms.txt 에도 이 문장이 쓰여요.
        </span>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor={field("description")} className="wds-label-1 text-label-strong">
          설명
        </label>
        <textarea
          id={field("description")}
          rows={5}
          value={item.description}
          onChange={(e) => onChange({ description: e.target.value })}
          className={inputClass}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="flex flex-col gap-1.5">
          <label htmlFor={field("category")} className="wds-label-1 text-label-strong">
            구분
          </label>
          <select
            id={field("category")}
            value={item.category}
            onChange={(e) => onChange({ category: e.target.value as PortfolioItem["category"] })}
            className={inputClass}
          >
            {CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {CATEGORY_LABEL[category]}
              </option>
            ))}
          </select>
        </div>
        {/* biome-ignore lint/a11y/useSemanticElements: 체크박스 묶음 라벨 — <fieldset> 은 카드 그리드 안에서 여백이 어긋나요 */}
        <div className="flex flex-col gap-1.5" role="group" aria-label={`${index + 1}번 플랫폼`}>
          <span className="wds-label-1 text-label-strong">플랫폼</span>
          <div className="flex items-center gap-4 py-2.5">
            {PLATFORMS.map((platform) => {
              const checked = item.platform.includes(platform);
              return (
                <label
                  key={platform}
                  className="wds-body-2 flex items-center gap-1.5 text-label-neutral"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      onChange({
                        platform: checked
                          ? item.platform.filter((p) => p !== platform)
                          : [...item.platform, platform],
                      })
                    }
                    className="h-4 w-4 accent-primary"
                  />
                  {PLATFORM_LABEL[platform]}
                </label>
              );
            })}
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="wds-label-1 text-label-strong">노출·출시 상태</span>
          <div className="flex items-center gap-4 py-2.5">
            <label className="wds-body-2 flex items-center gap-1.5 text-label-neutral">
              <input
                type="checkbox"
                checked={!(item.hidden ?? false)}
                onChange={(e) => onChange({ hidden: !e.target.checked })}
                className="h-4 w-4 accent-primary"
              />
              노출
            </label>
            <label className="wds-body-2 flex items-center gap-1.5 text-label-neutral">
              <input
                type="checkbox"
                checked={item.upcoming ?? false}
                onChange={(e) => onChange({ upcoming: e.target.checked })}
                className="h-4 w-4 accent-primary"
              />
              출시 전
            </label>
          </div>
          <span className="wds-caption-1 text-label-assistive">
            '노출' 을 끄면 지우지 않고 사이트에서만 빼요 — 카드·Hero 로고 띠·개수에서 모두 빠지고, 여기서
            다시 켜면 그대로 돌아와요. '출시 전' 은 문안에도 '출시 예정' 을 밝혀야 하고 운영 중 개수에서
            빠져요.
          </span>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="flex flex-col gap-1.5">
          <label htmlFor={field("url")} className="wds-label-1 text-label-strong">
            사이트 링크
          </label>
          <input
            id={field("url")}
            value={item.url ?? ""}
            placeholder="https://example.com"
            onChange={(e) => onChange({ url: e.target.value })}
            className={inputClass}
          />
        </div>
        <AssetField
          id={field("cover")}
          label="커버 이미지"
          placeholder="/portfolio/example.webp"
          value={item.cover ?? ""}
          token={token}
          onChange={(cover) => onChange({ cover })}
          onAuthError={onAuthError}
        />
        <AssetField
          id={field("logo")}
          label="로고"
          placeholder="/portfolio/logos/example.png"
          value={item.logo ?? ""}
          token={token}
          onChange={(logo) => onChange({ logo })}
          onAuthError={onAuthError}
        />
      </div>
      <p className="wds-caption-1 text-label-assistive">
        파일을 올리면 공유 오브젝트 스토리지에 저장되고 그 주소가 위 칸에 채워져요. 저장해야 사이트에
        반영돼요. `apps/web/public/portfolio/` 에 넣어 배포한 정적 경로도 그대로 쓸 수 있어요.
      </p>
    </li>
  );
}

export function PortfolioPanel({ token, onLogout }: { token: string; onLogout: () => void }) {
  const [config, setConfig] = useState<PortfolioDTO | null>(null);
  const [draft, setDraft] = useState<PortfolioItem[] | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);

  const handleApiError = useCallback(
    (err: unknown, fallback: string) => {
      if (err instanceof ApiError && err.status === 401) {
        onLogout();
        return;
      }
      setErrors([err instanceof ApiError ? err.message : fallback]);
    },
    [onLogout],
  );

  useEffect(() => {
    // 공개 `/portfolio` 가 아니라 어드민 읽기예요 — 공개 쪽은 숨긴 항목을 빼고 내려줘서
    // 그걸 읽으면 숨긴 항목이 편집 화면에서도 사라져 다시 켤 수가 없어요.
    api
      .get<PortfolioDTO>("/admin/portfolio", token)
      .then((next) => {
        setConfig(next);
        setDraft(next.items);
      })
      .catch((err) => handleApiError(err, "포트폴리오를 불러오지 못했어요."));
  }, [handleApiError, token]);

  const updateDraft = (next: PortfolioItem[]) => {
    setNotice("");
    setDraft(next);
  };

  const handleSave = async () => {
    if (!draft) return;
    const found = validatePortfolioItems(draft);
    setErrors(found);
    setNotice("");
    if (found.length > 0) return;

    setSaving(true);
    try {
      const published = await api.put<PortfolioDTO>("/admin/portfolio", { items: draft }, token);
      setConfig(published);
      setDraft(published.items);
      setNotice(`버전 ${published.version} 으로 저장했어요. 사이트에 바로 반영돼요.`);
    } catch (err) {
      handleApiError(err, "저장하지 못했어요.");
    } finally {
      setSaving(false);
    }
  };

  if (!draft || !config) {
    return (
      <div className="flex flex-col gap-3">
        {errors.map((e) => (
          <output key={e} className="wds-body-2 text-status-negative">
            {e}
          </output>
        ))}
        {errors.length === 0 && (
          <p className="wds-body-2 text-label-assistive">포트폴리오를 불러오는 중이에요…</p>
        )}
      </div>
    );
  }

  const shownCount = draft.filter((item) => !item.hidden).length;
  // About KPI·Hero 로고 띠가 세는 기준과 같아요 — 숨긴 항목은 방문자에겐 없는 항목이에요.
  const liveCount = draft.filter((item) => !item.upcoming && !item.hidden).length;

  return (
    <div className="flex flex-col gap-6">
      <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 bg-background-normal py-3">
        <div className="flex flex-wrap items-center gap-3">
          <p className="wds-body-2 text-label-neutral">
            현재 버전 <span className="font-semibold tabular-nums">{config.version}</span> ·{" "}
            {formatKst(config.updated_at)}
          </p>
          <span className="wds-label-1 rounded-full bg-background-alternative px-3 py-1 text-label-neutral">
            총 <span className="font-semibold tabular-nums">{draft.length}</span>개 · 노출{" "}
            <span className="font-semibold tabular-nums">{shownCount}</span>개 · 운영 중{" "}
            <span className="font-semibold tabular-nums">{liveCount}</span>개
          </span>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex h-11 items-center justify-center rounded-md bg-primary px-6 wds-body-2 font-semibold text-static-white transition-colors duration-200 ease-standard hover:bg-primary-strong disabled:opacity-50"
        >
          {saving ? "저장 중…" : "저장하기"}
        </button>
      </div>

      {errors.length > 0 && (
        <output className="flex flex-col gap-1 rounded-md bg-background-alternative px-4 py-3">
          {errors.map((e) => (
            <span key={e} className="wds-body-2 text-status-negative">
              {e}
            </span>
          ))}
        </output>
      )}
      {notice && (
        <output className="flex items-center gap-2 rounded-md bg-background-alternative px-4 py-3 wds-body-2 text-primary">
          <Icon name="circleCheckFill" size={16} />
          {notice}
        </output>
      )}

      <p className="wds-caption-1 text-label-assistive">
        위에서 아래 순서가 포트폴리오 섹션의 카드 순서예요. 저장하면 사이트 화면(카드·Hero 로고 띠·About
        지표)에 바로 반영되고, 크롤러용 본문(`noscript`·llms.txt)은 빌드 시점 스냅샷이라 다음 배포에
        따라와요.
      </p>

      <ul className="flex flex-col gap-4">
        {draft.map((item, index) => (
          <ItemCard
            key={`${item.id}-${index}`}
            item={item}
            index={index}
            total={draft.length}
            token={token}
            onAuthError={onLogout}
            onChange={(patch) =>
              updateDraft(draft.map((cur, i) => (i === index ? { ...cur, ...patch } : cur)))
            }
            onMove={(delta) => updateDraft(moveItem(draft, index, delta))}
            onRemove={() => updateDraft(draft.filter((_, i) => i !== index))}
          />
        ))}
      </ul>

      <button
        type="button"
        onClick={() => updateDraft([...draft, emptyItem()])}
        className="h-11 rounded-md bg-background-alternative px-4 wds-body-2 font-semibold text-label-neutral"
      >
        항목 추가
      </button>
    </div>
  );
}

export default PortfolioPanel;
